import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product.types';
import {useTranslation} from '@aboutyou/i18n';
import {useInitialLocation} from '@aboutyou/router';
import {
  COLORS,
  ContentPopper,
  ContentPopperProps,
  Drawer,
  DrawerBody,
  FONT_SIZES,
  GenericIconName,
  Icon,
  IconSize,
  Modal,
  ModalHeader,
  Text,
  UnstyledButton,
  Z_INDEXES,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {useFullSession} from '@aboutyou/user';
import {styled} from '@linaria/react';
import React, {FC} from 'react';
import {useTrackWalletContentView} from '../../../../backend-services/Wallet/utils/useTrackWalletContentView';
import {useWalletStateManager} from '../../../../backend-services/Wallet/utils/useWalletStateManager';
import {useSessionGender} from '../../../../shared-deprecated/sessionGender';
import couponWalletBackground from '../../assets/couponWalletBackground-2.svg';
import {useIsCouponWalletActive} from '../hooks/useIsCouponWalletActive';
import {CouponWalletBodyContent} from './CouponWalletBodyContent';
import {WalletBody as HighTierWalletBody} from './highTierWallet/WalletBody';

const BACK_ICON_SIZE = 'S';

interface CouponWalletProps {
  ContentPopperLayoutComponent: ContentPopperProps['LayoutComponent'];
  setSelectedProduct?: (product: Product | undefined) => void;
}

/**
 *
 * @param props
 * @returns
 * @deprecated shall be removed once coins is live
 */
export const CouponWallet: FC<CouponWalletProps> = ({
  ContentPopperLayoutComponent,
  setSelectedProduct,
}) => {
  useTrackWalletContentView();
  const shouldShowHeaderInBody = ContentPopperLayoutComponent !== Drawer;
  const isHighTierCouponsActive =
    useIsFeatureActive('high_tier_coupons').isActive;

  const {isOpen, cancel, hide} = useWalletStateManager();
  const fullSession = useFullSession();
  const isCouponWalletActive = useIsCouponWalletActive();
  const initialLocation = useInitialLocation();
  const {gender} = useSessionGender();

  const handleOnClose = () => {
    // On desktop, when the entry page is the deep link "/cw" and the gender is kids, the user gets
    // in an infinite loop when closing the coupon wallet, since history back is used, sending the user
    // to the home page, and then the kids category page redirect kicks in
    if (
      initialLocation.pathname === '/cw' &&
      gender === 'kids' &&
      ContentPopperLayoutComponent !== Modal
    ) {
      hide();
    } else {
      cancel();
    }
  };

  if (!fullSession || !isCouponWalletActive) {
    return null;
  }

  const headerContent = isHighTierCouponsActive ? null : (
    <Header hideBackButton />
  );

  const bodyContent = isHighTierCouponsActive ? (
    <HighTierWalletBody
      setSelectedProduct={setSelectedProduct}
      // only show the cross icon for the Drawer
      showCrossButton={ContentPopperLayoutComponent !== Modal}
    />
  ) : (
    <BodyContent
      shouldShowHeaderInBody={shouldShowHeaderInBody}
      setSelectedProduct={setSelectedProduct}
    />
  );

  return (
    <StyledContentPopper
      data-is-high-tier-coupons-active={isHighTierCouponsActive}
      LayoutComponent={ContentPopperLayoutComponent}
      onClose={handleOnClose}
      slideDirection="left"
      hideCloseButton
      open={isOpen}
      renderInPortal
      animated
      data-testid="couponWallet"
      preventSlideUpAnimation
      headerContent={
        !shouldShowHeaderInBody && headerContent && <>{headerContent}</>
      }
      bodyContent={bodyContent}
    />
  );
};

const BodyContent: FC<{
  shouldShowHeaderInBody: boolean;
  setSelectedProduct?: (product: Product | undefined) => void;
}> = ({shouldShowHeaderInBody, setSelectedProduct}) => (
  <Wrapper>
    {shouldShowHeaderInBody && <Header />}
    <CouponWalletBodyContent setSelectedProduct={setSelectedProduct} />
  </Wrapper>
);

const Header: FC<{hideBackButton?: boolean}> = ({hideBackButton}) => {
  const {t} = useTranslation();
  const {cancel} = useWalletStateManager();

  return (
    <HeaderWrapper data-testid="notificationCenter-header">
      {!hideBackButton && (
        <BackButton onClick={cancel} aria-label={t('Close coupon wallet')}>
          <Icon asset={GenericIconName.ArrowLeft} size={BACK_ICON_SIZE} />
        </BackButton>
      )}
      <TextContainer>
        <Title size="px20" weight="mediumHeavy">
          {t('Your Coupons')}
        </Title>
      </TextContainer>
    </HeaderWrapper>
  );
};

const Wrapper = styled.div`
  --button-left-padding: 13px;
  --button-right-padding: 9px;
  height: 100%;
  width: 100%;
`;

const HeaderWrapper = styled.div`
  align-items: center;
  display: flex;
  padding-block: 13px;
  position: sticky;
  top: 0;
  background-color: ${COLORS.White};
  z-index: ${Z_INDEXES.FLYOUT_STICKY_HEADER};
`;

const BackButton = styled(UnstyledButton)`
  padding-inline: var(--button-left-padding) var(--button-right-padding);
  z-index: 1;
`;

const TextContainer = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: center;
  padding-right: calc(
    var(--button-left-padding) + var(--button-right-padding) +
      ${IconSize[BACK_ICON_SIZE]}px
  );
`;

const Title = styled(Text)`
  line-height: 1.2;

  ${tabletAndUpLinaria(`
    font-size: ${FONT_SIZES.px16};
    line-height: 1.25;
  `)}
`;

const StyledContentPopper = styled(ContentPopper)<{
  $isHighTierCouponsActive?: boolean;
  animated: boolean;
}>`
  ${DrawerBody} {
    padding: 0;
    background-color: transparent;
  }

  ${ModalHeader} {
    display: none;
  }

  &[data-is-high-tier-coupons-active='true'] {
    overflow: hidden;

    background-color: ${COLORS.BackgroundCoupon};

    &::before {
      content: '';
      background-image: url(${couponWalletBackground});
      background-size: cover;
      inset: 0;
      position: fixed;
      z-index: -1;
    }
  }
`;
